/**
 * @fileoverview Internationalization (i18n) Configuration
 * @module i18n
 *
 * @description
 * Enterprise i18n setup using react-i18next and i18next. Provides multi-language
 * support with lazy-loaded translation namespaces, language detection, and
 * TypeScript type safety.
 *
 * Features:
 * - Multiple languages (en, fr, es, de, ja, zh)
 * - Lazy-loaded namespaces (common, errors, validation, components)
 * - Language detection (localStorage, browser, fallback)
 * - TypeScript type safety
 * - Interpolation and pluralization
 * - React Suspense integration
 * - Debug mode for development
 * - Missing key handler
 *
 * @example
 * ```typescript
 * // In components
 * import { useTranslation } from 'react-i18next';
 *
 * const { t, i18n } = useTranslation('common');
 *
 * return (
 *   <div>
 *     <h1>{t('welcome')}</h1>
 *     <button onClick={() => i18n.changeLanguage('fr')}>
 *       Français
 *     </button>
 *   </div>
 * );
 * ```
 *
 * @see https://react.i18next.com/
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

/**
 * Supported languages
 */
export const SUPPORTED_LANGUAGES = {
  en: 'English',
  fr: 'Français',
  gu: 'ગુજરાતી', // Gujarati
  hi: 'हिन्दी', // Hindi
} as const;

/**
 * Language codes type
 */
export type LanguageCode = keyof typeof SUPPORTED_LANGUAGES;

/**
 * Translation namespaces
 */
export const NAMESPACES = {
  COMMON: 'common',
  ERRORS: 'errors',
  VALIDATION: 'validation',
  COMPONENTS: 'components',
  AUTH: 'auth',
  DASHBOARD: 'dashboard',
} as const;

/**
 * Default language
 */
export const DEFAULT_LANGUAGE: LanguageCode = 'en';

/**
 * Default namespace
 */
export const DEFAULT_NAMESPACE = NAMESPACES.COMMON;

/**
 * Check if language is supported
 *
 * @param lang - Language code to check
 * @returns True if supported
 */
export function isSupportedLanguage(lang: string): lang is LanguageCode {
  return lang in SUPPORTED_LANGUAGES;
}

/**
 * Get current language
 *
 * @returns Current language code
 */
export function getCurrentLanguage(): LanguageCode {
  const lang = i18n.language || DEFAULT_LANGUAGE;
  return isSupportedLanguage(lang) ? lang : DEFAULT_LANGUAGE;
}

/**
 * Change language
 *
 * @param lang - Language code to switch to
 * @returns Promise resolving when language is changed
 *
 * @example
 * ```typescript
 * await changeLanguage('fr');
 * console.log('Language changed to French');
 * ```
 */
export async function changeLanguage(lang: LanguageCode): Promise<void> {
  if (!isSupportedLanguage(lang)) {
    console.warn(`[i18n] Language '${lang}' is not supported. Using default.`);
    lang = DEFAULT_LANGUAGE;
  }

  // Load language resources if not already loaded
  if (!hasTranslations(lang)) {
    await loadLanguageResources(lang);
  }

  // Change language and save to localStorage
  await i18n.changeLanguage(lang);
  localStorage.setItem('app-language', lang);

  // Update HTML lang attribute
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lang;
  }
}

/**
 * Get available languages
 *
 * @returns Array of language objects with code and name
 */
export function getAvailableLanguages(): Array<{ code: LanguageCode; name: string }> {
  return Object.entries(SUPPORTED_LANGUAGES).map(([code, name]) => ({
    code: code as LanguageCode,
    name,
  }));
}

/**
 * Initialize i18next
 *
 * @returns Promise resolving when i18n is initialized
 */
export async function initI18n(): Promise<void> {
  const isDev = import.meta.env.DEV;

  await i18n
    // Language detector (checks localStorage, navigator, etc.)
    .use(LanguageDetector)
    // React integration
    .use(initReactI18next)
    // Initialize
    .init({
      // Resources will be loaded via dynamic imports below
      resources: {},

      // Default language
      fallbackLng: DEFAULT_LANGUAGE,

      // Supported languages
      supportedLngs: Object.keys(SUPPORTED_LANGUAGES),

      // Default namespace
      defaultNS: DEFAULT_NAMESPACE,

      // Available namespaces
      ns: Object.values(NAMESPACES),

      // Language detection options
      detection: {
        // Order of detection methods
        order: ['localStorage', 'navigator', 'htmlTag'],

        // Cache in localStorage
        caches: ['localStorage'],

        // localStorage key
        lookupLocalStorage: 'app-language',
      },

      // Interpolation options
      interpolation: {
        // React already escapes values
        escapeValue: false,

        // Format function for custom formatting
        format: (value, format) => {
          if (format === 'uppercase') return value.toUpperCase();
          if (format === 'lowercase') return value.toLowerCase();
          if (format === 'capitalize') {
            return value.charAt(0).toUpperCase() + value.slice(1);
          }
          return value;
        },
      },

      // React options
      react: {
        // Use Suspense for loading translations
        useSuspense: true,

        // Bind i18n instance to component - ensures re-render on language change
        bindI18n: 'languageChanged',

        // Bind i18n store to component - ensures re-render on resource changes
        bindI18nStore: '',

        // Re-render on language change without empty nodes
        transEmptyNodeValue: '',

        // Default translation component props
        transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p'],

        // Ensure components update smoothly on language change
        transSupportBasicHtmlNodes: true,
        transWrapTextNodes: '',
      },

      // Debug mode in development
      debug: false, // Set to false to reduce console noise

      // Missing key handler
      saveMissing: isDev,
      missingKeyHandler: (lngs, ns, key) => {
        if (isDev) {
          console.warn(`[i18n] Missing translation: [${ns}] ${key} (${lngs.join(', ')})`);
        }
      },

      // Preload languages
      preload: [DEFAULT_LANGUAGE],

      // Lazy load namespaces
      partialBundledLanguages: true,

      // Suppress backend warning since we load resources dynamically
      initImmediate: false,
    });

  // Load default language translations dynamically
  await loadLanguageResources(DEFAULT_LANGUAGE);

  // If current language is different from default, load it too
  const currentLang = getCurrentLanguage();
  if (currentLang !== DEFAULT_LANGUAGE) {
    await loadLanguageResources(currentLang);
  }
}

/**
 * Load language resources dynamically
 *
 * @param lang - Language code
 * @returns Promise resolving when resources are loaded
 */
export async function loadLanguageResources(lang: LanguageCode): Promise<void> {
  try {
    // Dynamically import all namespace files for the language
    const modules = await Promise.all([
      import(`./locales/${lang}/common.json`),
      import(`./locales/${lang}/errors.json`),
      import(`./locales/${lang}/validation.json`),
      import(`./locales/${lang}/components.json`),
      import(`./locales/${lang}/auth.json`),
      import(`./locales/${lang}/dashboard.json`),
    ]);

    // Add resources to i18n
    const namespaces = Object.values(NAMESPACES);
    modules.forEach((module, index) => {
      i18n.addResourceBundle(lang, namespaces[index], module.default || module, true, true);
    });

    if (import.meta.env.DEV) {
      console.log(`[i18n] Loaded resources for language: ${lang}`);
    }
  } catch (error) {
    console.error(`[i18n] Failed to load resources for language: ${lang}`, error);

    // Fall back to default language if current language fails
    if (lang !== DEFAULT_LANGUAGE) {
      await loadLanguageResources(DEFAULT_LANGUAGE);
    }
  }
}

/**
 * Add translations at runtime
 *
 * @param lang - Language code
 * @param namespace - Namespace
 * @param resources - Translation resources
 *
 * @example
 * ```typescript
 * addTranslations('en', 'custom', {
 *   'myKey': 'My Value'
 * });
 * ```
 */
export function addTranslations(
  lang: LanguageCode,
  namespace: string,
  resources: Record<string, string | object>
): void {
  i18n.addResourceBundle(lang, namespace, resources, true, true);
}

/**
 * Check if translations are loaded for a language
 *
 * @param lang - Language code
 * @param namespace - Optional namespace to check
 * @returns True if loaded
 */
export function hasTranslations(lang: LanguageCode, namespace?: string): boolean {
  if (namespace) {
    return i18n.hasResourceBundle(lang, namespace);
  }

  return i18n.hasResourceBundle(lang, DEFAULT_NAMESPACE);
}

/**
 * Reload translations for current language
 *
 * @returns Promise resolving when reloaded
 */
export async function reloadTranslations(): Promise<void> {
  const currentLang = getCurrentLanguage();
  await loadLanguageResources(currentLang);
}

/**
 * Format a date according to current locale
 *
 * @param date - Date to format
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted date string
 *
 * @example
 * ```typescript
 * formatDate(new Date(), { dateStyle: 'long' })
 * // English: "January 1, 2025"
 * // French: "1 janvier 2025"
 * ```
 */
export function formatDate(
  date: Date | number | string,
  options?: Intl.DateTimeFormatOptions
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const lang = getCurrentLanguage();

  return new Intl.DateTimeFormat(lang, options).format(dateObj);
}

/**
 * Format a number according to current locale
 *
 * @param value - Number to format
 * @param options - Intl.NumberFormat options
 * @returns Formatted number string
 *
 * @example
 * ```typescript
 * formatNumber(1234.56, { style: 'currency', currency: 'USD' })
 * // English: "$1,234.56"
 * // French: "1 234,56 $US"
 * ```
 */
export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  const lang = getCurrentLanguage();
  return new Intl.NumberFormat(lang, options).format(value);
}

/**
 * Get direction for current language (LTR or RTL)
 *
 * @returns 'ltr' or 'rtl'
 */
export function getLanguageDirection(): 'ltr' | 'rtl' {
  const lang = getCurrentLanguage();
  // Add RTL languages as needed (ar, he, fa, ur, etc.)
  const rtlLanguages = ['ar', 'he', 'fa', 'ur'];
  return rtlLanguages.includes(lang) ? 'rtl' : 'ltr';
}

// Export i18n instance
export default i18n;

// Export useTranslation hook from react-i18next
export { useTranslation } from 'react-i18next';
